import { parisToday } from "@erp/shared";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Download, Scale } from "lucide-react";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Page, PageHeader, Panel } from "~/components/ui/page";
import { EmptyState, ErrorState, Skeleton } from "~/components/ui/states";
import { StockTabs } from "~/features/stock/stock";
import { api, unwrap } from "~/lib/api";
import { csvAmount, downloadCsv } from "~/lib/csv";
import { intlLocale } from "@erp/i18n/format";
import { cn } from "~/lib/cn";
import { useFormat, useLocale, useT } from "~/lib/i18n";

const search = z.object({ at: z.iso.date().optional() });
export const Route = createFileRoute("/_app/stock/valuation")({ validateSearch: search, component: Valuation });

function Valuation() {
  const t = useT();
  const format = useFormat();
  const [locale] = useLocale();
  const navigate = useNavigate({ from: Route.fullPath });
  const { at } = Route.useSearch();
  const today = parisToday();
  const query = useQuery({
    queryKey: ["stock", "valuation", at ?? "now"],
    queryFn: () => unwrap(api.stock.valuation.get({ query: at ? { at } : {} })),
  });

  const exportCsv = () => {
    if (!query.data) return;
    downloadCsv(
      t("stock.valuation.filename", { date: at ?? today }),
      [
        t("products.fields.sku"),
        t("stock.fields.product"),
        t("products.fields.productType"),
        t("stock.valuation.quantity"),
        t("stock.fields.unitCost"),
        t("stock.valuation.purchaseValue"),
        t("stock.valuation.saleValue"),
      ],
      query.data.items.map((r) => [
        r.sku,
        r.title,
        r.productType,
        r.quantity,
        csvAmount(r.costCents, intlLocale(locale)),
        csvAmount(r.purchaseValueCents, intlLocale(locale)),
        csvAmount(r.saleValueCents, intlLocale(locale)),
      ]),
    );
  };

  const kpis = query.data
    ? [
        { label: t("stock.valuation.quantity"), value: format.number(query.data.totals.quantity) },
        { label: t("stock.valuation.purchaseValue"), value: format.money(query.data.totals.purchaseValueCents) },
        { label: t("stock.valuation.saleValue"), value: format.money(query.data.totals.saleValueCents) },
      ]
    : null;

  return (
    <Page>
      <PageHeader
        title={t("stock.title")}
        actions={
          <Button onClick={exportCsv} disabled={!query.data?.items.length}>
            <Download />
            {t("stock.valuation.export")}
          </Button>
        }
      />
      <StockTabs />
      <div className="mb-5 flex flex-wrap items-end gap-6">
        <Field label={t("stock.valuation.at")} className="w-48">
          <Input
            type="date"
            max={today}
            value={at ?? today}
            onChange={(e) =>
              void navigate({
                search: { at: e.target.value && e.target.value !== today ? e.target.value : undefined },
                replace: true,
              })
            }
          />
        </Field>
        {kpis ? (
          <dl className="flex flex-wrap gap-x-10 gap-y-3">
            {kpis.map((k) => (
              <div key={k.label}>
                <dt className="text-xs text-fg-3">{k.label}</dt>
                <dd className="font-display text-2xl font-medium tabular">{k.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <Skeleton className="h-12 w-96" />
        )}
      </div>
      {query.data && query.data.totals.negativeArticles > 0 ? (
        <p className="mb-4 text-sm text-warning">
          {t("stock.valuation.negativeNote", {
            count: query.data.totals.negativeArticles,
            quantity: format.number(query.data.totals.negativeQuantity),
          })}
        </p>
      ) : null}
      <Panel>
        {query.isPending ? (
          <Skeleton className="m-5 h-40" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <EmptyState icon={<Scale />} title={t("stock.valuation.emptyTitle")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-line text-[12.5px] text-fg-3">
                  <th className="h-10 pl-5 text-left font-medium">{t("stock.fields.product")}</th>
                  <th className="text-right font-medium">{t("stock.valuation.quantity")}</th>
                  <th className="text-right font-medium">{t("stock.fields.unitCost")}</th>
                  <th className="text-right font-medium">{t("stock.valuation.purchaseValue")}</th>
                  <th className="pr-5 text-right font-medium">{t("stock.valuation.saleValue")}</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((r) => (
                  <tr key={r.variantId} className="border-b border-line last:border-b-0">
                    <td className="py-2.5 pl-5">
                      <p className="font-medium">{r.title}</p>
                      <p className="text-xs text-fg-3 tabular">{r.sku}</p>
                    </td>
                    <td className={cn("text-right tabular", r.negative && "text-danger")}>{format.number(r.quantity)}</td>
                    <td className="text-right text-fg-2 tabular">{format.money(r.costCents)}</td>
                    <td className="text-right tabular">{format.money(r.purchaseValueCents)}</td>
                    <td className="pr-5 text-right tabular">{format.money(r.saleValueCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </Page>
  );
}
